import { useEffect, useState } from "react";

export const InteractiveBackground = () => {
    const [isMobile, setIsMobile] = useState(false);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkMobile();
        window.addEventListener("resize", checkMobile, { passive: true });

        return () => {
            window.removeEventListener("resize", checkMobile);
        };
    }, []);

    // Mouse tracking only on desktop
    useEffect(() => {
        if (isMobile) return;

        let rafId: number;
        let lastX = 0;
        let lastY = 0;

        const handleMouseMove = (e: MouseEvent) => {
            lastX = e.clientX;
            lastY = e.clientY;
        };

        const updatePosition = () => {
            setMousePos({ x: lastX, y: lastY });
            rafId = requestAnimationFrame(updatePosition);
        };

        window.addEventListener("mousemove", handleMouseMove, { passive: true });
        rafId = requestAnimationFrame(updatePosition);

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            cancelAnimationFrame(rafId);
        };
    }, [isMobile]);

    // Mobile: Simple, performant static background
    if (isMobile) {
        return (
            <div
                className="fixed inset-0 pointer-events-none z-0 bg-[#050505]"
                style={{ contain: 'strict' }}
            >
                {/* Simple gradient - no blur, no animations */}
                <div className="absolute inset-0 bg-gradient-to-b from-neutral-950 via-[#0a0a0a] to-[#050505]" />

                {/* Static subtle glow - no animation, reduced opacity, no blur filter */}
                <div
                    className="absolute top-[-20%] left-[-20%] w-[80vw] h-[80vw] rounded-full"
                    style={{
                        background: 'radial-gradient(circle, hsl(260 90% 65% / 0.08) 0%, transparent 70%)',
                    }}
                />
                <div
                    className="absolute bottom-[-20%] right-[-20%] w-[80vw] h-[80vw] rounded-full"
                    style={{
                        background: 'radial-gradient(circle, hsl(190 90% 50% / 0.05) 0%, transparent 70%)',
                    }}
                />

                {/* Simple grid overlay - no mask for better performance */}
                <div
                    className="absolute inset-0 opacity-30"
                    style={{
                        backgroundImage: 'linear-gradient(to right, rgba(128,128,128,0.03) 1px, transparent 1px), linear-gradient(to bottom, rgba(128,128,128,0.03) 1px, transparent 1px)',
                        backgroundSize: '50px 50px',
                    }}
                />
            </div>
        );
    }

    // Desktop: Full interactive background with animations
    return (
        <div
            className="fixed inset-0 pointer-events-none z-0 overflow-hidden bg-[#050505]"
            style={{ contain: 'strict' }}
        >
            {/* 1. Base Gradient */}
            <div className="absolute inset-0 bg-gradient-to-b from-neutral-950 via-[#0a0a0a] to-[#050505]" />

            {/* 2. Ambient Glows - with animations and blur */}
            <div
                className="absolute top-[-10%] left-[-10%] w-[60vw] h-[60vw] rounded-full bg-primary/15 animate-ambient-glow-1"
                style={{
                    filter: 'blur(120px)',
                    willChange: 'transform, opacity',
                    transform: 'translateZ(0)',
                }}
            />
            <div
                className="absolute bottom-[-10%] right-[-10%] w-[60vw] h-[60vw] rounded-full bg-secondary/10 animate-ambient-glow-2"
                style={{
                    filter: 'blur(120px)',
                    willChange: 'transform, opacity',
                    transform: 'translateZ(0)',
                }}
            />

            {/* 3. Tech Grid Overlay */}
            <div
                className="absolute inset-0"
                style={{
                    backgroundImage: 'linear-gradient(to right, rgba(128,128,128,0.03) 1px, transparent 1px), linear-gradient(to bottom, rgba(128,128,128,0.03) 1px, transparent 1px)',
                    backgroundSize: '50px 50px',
                    maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, #000 60%, transparent 100%)',
                    WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, #000 60%, transparent 100%)',
                }}
            />

            {/* 4. Scanning Grid Beam */}
            <div
                className="absolute inset-0 animate-scan-beam"
                style={{
                    background: 'linear-gradient(to right, transparent 0%, rgba(65,105,225,0.03) 50%, transparent 100%)',
                    width: '200%',
                    transform: 'skewX(-20deg) translateZ(0)',
                    willChange: 'transform',
                }}
            />

            {/* 5. Interactive Mouse Spotlight */}
            <div
                className="absolute w-[500px] h-[500px] rounded-full pointer-events-none"
                style={{
                    background: 'radial-gradient(circle, rgba(139,92,246,0.08) 0%, transparent 70%)',
                    left: mousePos.x,
                    top: mousePos.y,
                    transform: 'translate(-50%, -50%) translateZ(0)',
                    willChange: 'left, top',
                    transition: 'left 0.15s ease-out, top 0.15s ease-out',
                }}
            />

            {/* 6. Noise Texture */}
            <div
                className="absolute inset-0 opacity-[0.012] mix-blend-overlay pointer-events-none"
                style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
                }}
            />
        </div>
    );
};
